/**
 * CountModal — Crear conteo de inventario.
 * Seleccionar bodega y fecha, las líneas se auto-populan al iniciar el conteo.
 */
import { todayISO } from '../../../lib/utils'
import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { Loader2, ClipboardList } from 'lucide-react'
import DatePicker from '@/components/DatePicker'
import { labelCls, errorCls } from '@/lib/formStyles'
import { useWarehouses } from '../hooks/useInventory'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { Modal } from '@/components/ds/Modal'

interface Props {
  open: boolean
  onClose: () => void
  onSubmit: (data: any) => void
  isPending: boolean
}

interface FormData {
  warehouse: string
  count_date: string
  notes: string
}

export default function CountModal({ open, onClose, onSubmit, isPending }: Props) {
  const { register, handleSubmit, reset, control, formState: { errors } } = useForm<FormData>()
  const { data: whData } = useWarehouses({ active: 'true', page_size: 9999 })
  const warehouses = whData?.warehouses || []

  useEffect(() => {
    if (open) {
      reset({
        warehouse: '',
        count_date: todayISO(),
        notes: '',
      })
    }
  }, [open, reset])

  useEffect(() => {
    if (!open) return
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [open, onClose])

  const handleFormSubmit = (data: FormData) => {
    onSubmit({
      warehouse: data.warehouse,
      count_date: data.count_date,
      status: 'in_progress',
      notes: data.notes,
      lines: [],  // Se auto-populan en el backend al cambiar a in_progress
    })
  }

  if (!open) return null

  return (
    <Modal open onClose={onClose} title={<span className="flex items-center gap-2"><ClipboardList size={18} className="text-primary" /> Nuevo conteo de inventario</span>} size="lg">
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-3">
        <SelectField
          dense
          label="Bodega"
          required
          selectProps={register('warehouse', { required: 'Seleccione una bodega.' })}
          error={errors.warehouse?.message}
        >
          <option value="">Seleccionar bodega...</option>
          {warehouses.map(w => (
          <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
          ))}
        </SelectField>

        <div>
          <label htmlFor="countmodal-count-date" className={labelCls}>Fecha del conteo <span className="text-danger-solid">*</span></label>
          <Controller name="count_date" control={control} rules={{ required: 'Ingrese la fecha.' }}
            render={({ field }) => <DatePicker id="countmodal-count-date" value={field.value} onChange={field.onChange} onBlur={field.onBlur} required />} />
          {errors.count_date && <p className={errorCls}>{errors.count_date.message}</p>}
        </div>

        <TextField
          dense
          label="Observaciones"
          multiline
          rows={2}
          placeholder="Motivo del conteo, notas..."
          inputProps={register('notes')}
        />

        <div className="p-3 bg-warning-surface border border-warning-border rounded-lg text-xs text-warning-content">
          Al crear el conteo, se cargarán automáticamente todos los productos con stock en la bodega seleccionada.
          Luego podrá ingresar las cantidades contadas para cada producto.
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-divider">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-divider rounded-lg hover:bg-soft">Cancelar</button>
          <button type="submit" disabled={isPending}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-60">
            {isPending && <Loader2 size={14} className="animate-spin" />}
            Iniciar conteo
          </button>
        </div>
      </form>
    </Modal>
  )
}
